import React, { useEffect } from "react";
import { Alert, Container } from "../../../components";
import { useQueryParam } from "../../../components/hooks";
import { RadioGroup, Table, Text, Heading, Button, Icon } from "@telia/teddy";
import _isEmpty from "lodash/isEmpty";
import { connect, useDispatch, useSelector } from "react-redux";
import { FORM_ID, REFILL_LABELS } from "../../../../../constants";
import PAGES from "../../../../../constants/pages";
import { RootState } from "../../../../../models";
import { setNegativeFlash } from "../../../app/state/generalActions";
import { getErrorMessage } from "../../../app/state/selectors";
import AppLinks from "../../../components/components/molecules/AppLinks/AppLinks";
import { trackFunnelEvent } from "../../../google-analytics";
import { selectors as LOADING_SELECTORS } from "../../../loading";
import FAQ from "../../components/molecules/Faq/Faq";
import RefillPrepaid from "../../components/organisms/RefillPrepaidForm/RefillPrepaidForm";
import { verifyRefillPhoneNumber } from "../../state/actions/refillActions";
import { getPrepaidTextBlocks, isNumberVerified } from "../../state/selectors";
import TEXT from "../../text-consts";
import "./Refill.less";

interface StateProps {
    isFetchingData: boolean;
    isNumberValid: boolean;
    logicalErrorMessage?: string;
}

type PrepaidRefillProps = StateProps;

type RefillMethod = "creditCard" | "refillCode";

const PrepaidRefill: React.FC<PrepaidRefillProps> = ({
    // Redux
    isFetchingData,
    isNumberValid,
    logicalErrorMessage,
}) => {
    const dispatch = useDispatch();

    // Trigger GA funnel
    useEffect(() => {
        trackFunnelEvent({ step: "1", label: REFILL_LABELS[0], name: "Refill - Påfyll kontantkort" });
    }, []);

    const textBlocks = useSelector((state: RootState) => getPrepaidTextBlocks(state));
    const isPaymentFailed = useQueryParam("paymentFailed");
    const isSubmitFailed = useQueryParam("submitFailed");
    const [refillMethod, setRefillMethod] = React.useState<RefillMethod>("creditCard");
    useEffect(() => {
        if (isNumberValid) {
            window.location.assign(PAGES.PREPAID.REFILL.CHOOSE_PACKAGE.URL);
        }
    }, [isNumberValid]);

    useEffect(() => {
        // Check if paymentFailed (kept persistent since other requests would otherwise clear it on dispatch)
        if (isPaymentFailed) {
            dispatch(setNegativeFlash(TEXT.REFILL.ERROR.PAYMENT, undefined, true));
        }
        if (isSubmitFailed) {
            dispatch(setNegativeFlash(TEXT.REFILL.ERROR.PAYMENT, undefined, true));
        }
    }, [isPaymentFailed, isSubmitFailed, setNegativeFlash]);

    return (
        <div className="prepaid-refill">
            {logicalErrorMessage && (
                <Container size="small">
                    <Alert kind="warning">{logicalErrorMessage}</Alert>
                </Container>
            )}
            <div className="prepaid-refill__heading-container">
                <div className="prepaid-refill__heading-inner">
                    <div className="prepaid-refill__heading-wrapper">
                        <Heading mb="100" as="h1" variant="title-600" className="prepaid-refill__heading">
                            {TEXT.REFILL.HEADING}
                        </Heading>
                        <Text mb="100">{TEXT.REFILL.INGRESS}</Text>
                    </div>
                </div>
            </div>
            <RadioGroup
                defaultValue="creditCard"
                name="radio-group"
                onValueChange={(e) => setRefillMethod(e as RefillMethod)}
                orientation="horizontal"
            >
                <RadioGroup.GroupLabel>{TEXT.REFILL.TABLE.RADIOGROUP.HEADING}</RadioGroup.GroupLabel>
                <RadioGroup.Item>
                    <RadioGroup.Trigger value="creditCard" />
                    <RadioGroup.Label>{TEXT.REFILL.TABLE.RADIOGROUP.OPTION1}</RadioGroup.Label>
                </RadioGroup.Item>
                <RadioGroup.Item>
                    <RadioGroup.Trigger value="refillCode" />
                    <RadioGroup.Label>{TEXT.REFILL.TABLE.RADIOGROUP.OPTION2}</RadioGroup.Label>
                </RadioGroup.Item>
            </RadioGroup>
            {refillMethod === "creditCard" && (
                <div className="beta-checkout__section prepaid-refill-form">
                    <RefillPrepaid onSubmitDetails={() => dispatch(verifyRefillPhoneNumber())} />
                    <Button variant="primary" disabled={isFetchingData} type="submit" form={FORM_ID.REFILL_PREPAID}>
                        {TEXT.REFILL.BUTTON.CHECK_PHONE_NUMBER} <Icon name="arrow-right" />
                    </Button>
                </div>
            )}
            {refillMethod === "refillCode" && (
                <Container className="beta-checkout__section prepaid-refill-table" size="small" whiteBg noPadding>
                    <Heading mb="100" as="h2" variant="title-200">
                        {TEXT.REFILL.TABLE.REFILLPHONENUMBER}
                    </Heading>
                    <Table>
                        <Table.Header>
                            <Table.Row>
                                <Table.Head>{TEXT.REFILL.TABLE.HEADING1}</Table.Head>
                                <Table.Head>{TEXT.REFILL.TABLE.HEADING2}</Table.Head>
                            </Table.Row>
                        </Table.Header>
                        <Table.Body>
                            <Table.Row>
                                <Table.Cell>{TEXT.REFILL.TABLE.ROW1.CELL1}</Table.Cell>
                                <Table.Cell>{TEXT.REFILL.TABLE.ROW1.CELL2}</Table.Cell>
                            </Table.Row>
                            <Table.Row>
                                <Table.Cell>{TEXT.REFILL.TABLE.ROW2.CELL1}</Table.Cell>
                                <Table.Cell>{TEXT.REFILL.TABLE.ROW2.CELL2}</Table.Cell>
                            </Table.Row>
                        </Table.Body>
                    </Table>
                </Container>
            )}
            {_isEmpty(textBlocks) &&
                textBlocks.map((block) => (
                    <Container
                        className="prepaid-refill__textblock"
                        style={{ backgroundColor: block.contentBackgroundColor }}
                        key={`prepaid-textblock-${block.heading}`}
                        size="small"
                    >
                        <Heading mb="100" as="h2" variant="title-200">
                            {block.heading}
                        </Heading>
                        <div dangerouslySetInnerHTML={{ __html: block.content }} />
                    </Container>
                ))}
            <AppLinks
                body="Allerede nå kan du bruke Telia-appen til å fylle på kontantkortet ditt, enten med vipps eller
                    bankkort. I tillegg får du full oversikt over andre Telia-produkter hvis du har flere abonnement hos
                    oss (gjelder både mobilabonnement, bredbånd og TV)."
            />
            <br />
            <br />
            <FAQ />
        </div>
    );
};

const mapStateToProps: (state: RootState) => StateProps = (state) => ({
    // Loading
    isFetchingData: LOADING_SELECTORS.isLoading(state),
    isNumberValid: isNumberVerified(state),
    logicalErrorMessage: getErrorMessage(state),
});

export default connect(mapStateToProps)(PrepaidRefill);
